import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  sectionMargins,
  brandColours,
  standardTransition,
} from '../styles';
import { Container, Heading, Button } from './ui';

const StyledImageContent = styled.section`
  position: relative;
  ${sectionMargins(undefined, '120px')};

  &:before {
    ${minBreakpointQuery.xxlarge`
      ${({ flip }) => {
        if (flip) {
          return css`
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            height: 540px;
            width: 270px;
            background-color: ${brandColours.primary};
            border-radius: 0 270px 270px 0;
            opacity: 0.4;
            transform: translateX(-75%);
          `;
        }
      }}
    `}
  }
`;

const StyledInner = styled.div`
  ${minBreakpointQuery.smedium`
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    ${({ flip }) => {
      if (flip) {
        return css`
          flex-direction: row-reverse;
        `;
      }
    }}
  `}
`;

const StyledBlock = styled.div`
  ${minBreakpointQuery.smedium`
    width: 50%;
  `}

  &:last-child {
    ${minBreakpointQuery.smedium`
      align-self: center;
    `}
  }
`;

const StyledImage = styled(GatsbyImage)`
  margin: auto;
  width: 100%;
`;

const StyledContent = styled.div`
  ${maxBreakpointQuery.smedium`
    padding-top: 30px;
  `}

  ${minBreakpointQuery.smedium`
    padding-${({ flip }) => (flip ? 'right' : 'left')}: 40px;
  `}

  ${minBreakpointQuery.mlarge`
    padding-${({ flip }) => (flip ? 'right' : 'left')}: 50px;
  `}

  ${minBreakpointQuery.xlarge`
    padding-${({ flip }) => (flip ? 'right' : 'left')}: 60px;
  `}
`;

const StyledHeading = styled(Heading)`
  margin-bottom: 15px;

  ${minBreakpointQuery.small`
    margin-bottom: 20px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 25px;
  `}
`;

const StyledText = styled.div`
  p {
    margin-bottom: 20px;
    line-height: 1.9;

    ${minBreakpointQuery.small`
      margin-bottom: 24px;
    `}

    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    position: relative;
    color: ${brandColours.primary};
    border-bottom: 1px solid transparent;
    ${standardTransition('border-color')};

    &:hover {
      border-bottom-color: ${brandColours.primary};
    }
  }
`;

const StyledButton = styled(Button)`
  margin-top: 15px;

  ${minBreakpointQuery.small`
    margin-top: 20px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 25px;
  `}
`;

const ImageContent = ({
  image: { gatsbyImageData, alt },
  heading,
  text,
  link,
  flip,
}) => (
  <StyledImageContent flip={flip}>
    <Container>
      <StyledInner flip={flip}>
        <StyledBlock>
          <StyledImage image={gatsbyImageData} alt={alt} />
        </StyledBlock>
        <StyledBlock>
          <StyledContent flip={flip}>
            <StyledHeading removeWave={true}>{heading}</StyledHeading>
            {text && <StyledText dangerouslySetInnerHTML={{ __html: text }} />}
            {link && (
              <StyledButton to={link.page.slug} linkData={link.page}>
                {link.text}
              </StyledButton>
            )}
          </StyledContent>
        </StyledBlock>
      </StyledInner>
    </Container>
  </StyledImageContent>
);

export default ImageContent;
